import { Fragment, type JSX, type ReactNode } from "react";
import { marked, type Token, type Tokens } from "marked";

/**
 * Renders markdown WITHOUT ever producing an HTML string.
 *
 * WHY NOT `marked.parse()` + a sanitiser: the content here is model-written
 * text that quotes the database, and `company.legalName` is tenant-controlled.
 * That means attacker-influenced text reaches a ROOT user's browser. The usual
 * answer — render to HTML, then sanitise — makes safety depend on the
 * sanitiser being present, configured, and kept current; `marked` v17 removed
 * its own `sanitize` option, and the portal declares no sanitiser at all.
 *
 * So this uses `marked.lexer()` for parsing (battle-tested) and maps the
 * tokens to React elements. Text lands in text nodes, which React escapes, so
 * there is no HTML string for anything to be injected into. `html` tokens —
 * exactly what a `<script>` or `<img onerror>` in the data becomes — are
 * rendered as their literal characters.
 *
 * There is deliberately no `dangerouslySetInnerHTML` in this file, and a test
 * asserts that stays true.
 */

function InlineTokens({ tokens }: { tokens?: Token[] }): JSX.Element {
  if (!tokens || tokens.length === 0) return <></>;
  return (
    <>
      {tokens.map((token, i) => (
        <Fragment key={i}>{renderInline(token)}</Fragment>
      ))}
    </>
  );
}

function renderInline(token: Token): ReactNode {
  switch (token.type) {
    case "strong":
      return (
        <strong>
          <InlineTokens tokens={(token as Tokens.Strong).tokens} />
        </strong>
      );
    case "em":
      return (
        <em>
          <InlineTokens tokens={(token as Tokens.Em).tokens} />
        </em>
      );
    case "del":
      return (
        <del>
          <InlineTokens tokens={(token as Tokens.Del).tokens} />
        </del>
      );
    case "codespan":
      return (
        <code className="rounded bg-[var(--fx-s-raised-hover)] px-1 py-0.5 text-[0.85em]">
          {(token as Tokens.Codespan).text}
        </code>
      );
    case "br":
      return <br />;
    case "link": {
      const link = token as Tokens.Link;
      // Only http(s). A `javascript:` href is script execution by another
      // name, and the model quotes tenant-controlled text.
      const safe = /^https?:\/\//i.test(link.href);
      if (!safe) return <>{link.text}</>;
      return (
        <a
          href={link.href}
          target="_blank"
          rel="noopener noreferrer"
          className="underline underline-offset-2"
        >
          <InlineTokens tokens={link.tokens} />
        </a>
      );
    }
    // `html` is what raw markup in the DATA becomes. Rendering `raw` puts the
    // literal characters on screen — this is the XSS defence, not an oversight.
    case "html":
      return <>{(token as Tokens.HTML).raw}</>;
    default:
      return <>{(token as { raw?: string; text?: string }).text ?? (token as { raw?: string }).raw ?? ""}</>;
  }
}

function renderBlock(token: Token, key: number): ReactNode {
  switch (token.type) {
    case "heading": {
      const h = token as Tokens.Heading;
      const sizes = ["text-xl", "text-lg", "text-base", "text-sm", "text-sm", "text-sm"];
      const Tag = (`h${Math.min(h.depth, 6)}`) as "h1";
      return (
        <Tag key={key} className={`mt-4 mb-2 font-semibold ${sizes[h.depth - 1] ?? "text-sm"}`}>
          <InlineTokens tokens={h.tokens} />
        </Tag>
      );
    }
    case "paragraph":
      return (
        <p key={key} className="my-2 leading-relaxed">
          <InlineTokens tokens={(token as Tokens.Paragraph).tokens} />
        </p>
      );
    case "list": {
      const list = token as Tokens.List;
      const Tag = list.ordered ? "ol" : "ul";
      return (
        <Tag
          key={key}
          className={`my-2 ml-5 space-y-1 ${list.ordered ? "list-decimal" : "list-disc"}`}
        >
          {list.items.map((item, i) => (
            <li key={i}>
              <InlineTokens tokens={item.tokens} />
            </li>
          ))}
        </Tag>
      );
    }
    case "table": {
      const table = token as Tokens.Table;
      return (
        // Wide results must scroll inside the answer, not stretch the page.
        <div key={key} className="my-3 overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="border-b border-[var(--fx-ink-200)]">
                {table.header.map((cell, i) => (
                  <th key={i} className="px-2 py-1.5 text-left font-semibold">
                    <InlineTokens tokens={cell.tokens} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {table.rows.map((row, r) => (
                <tr key={r} className="border-b border-[var(--fx-ink-100)]">
                  {row.map((cell, c) => (
                    <td key={c} className="px-2 py-1.5 align-top">
                      <InlineTokens tokens={cell.tokens} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    case "code":
      return (
        <pre
          key={key}
          className="my-3 overflow-x-auto rounded-[var(--fx-r-md)] bg-[var(--fx-s-raised-hover)] p-3 text-xs"
        >
          <code>{(token as Tokens.Code).text}</code>
        </pre>
      );
    case "blockquote":
      return (
        <blockquote
          key={key}
          className="my-2 border-l-2 border-[var(--fx-ink-300)] pl-3 text-[var(--fx-ink-500)]"
        >
          <SafeMarkdown markdown={(token as Tokens.Blockquote).raw.replace(/^> ?/gm, "")} />
        </blockquote>
      );
    case "hr":
      return <hr key={key} className="my-4 border-[var(--fx-ink-200)]" />;
    case "space":
      return null;
    // Same reasoning as the inline case: literal characters, never markup.
    default:
      return (
        <p key={key} className="my-2 whitespace-pre-wrap">
          {(token as { raw?: string }).raw ?? ""}
        </p>
      );
  }
}

export function SafeMarkdown({ markdown }: { markdown: string }): JSX.Element {
  const tokens = marked.lexer(markdown);
  return <>{tokens.map((token, i) => renderBlock(token, i))}</>;
}
